type Task<T> = (signal?: AbortSignal) => Promise<T>;

interface Job {
  run: () => Promise<void>;
  signal?: AbortSignal;
  reject: (reason: unknown) => void;
}

interface EnqueueOptions {
  signal?: AbortSignal;
  /** true 면 대기열 맨 앞에 끼워 넣는다. 진행 중인 요청을 밀어내지는 않는다. */
  priority?: boolean;
}

const abortError = () => new DOMException("요청이 취소되었습니다.", "AbortError");

/**
 * 한 번에 하나만 실행하는 직렬 큐.
 * 앞선 작업이 끝난(성공·실패·취소) 뒤에만 다음 작업을 시작한다 — 동시 실행 수는 항상 1 이하.
 */
const createSerialQueue = () => {
  const waiting: Job[] = [];
  let isRunning = false;

  const pump = async () => {
    if (isRunning) return;
    isRunning = true;
    try {
      for (let job = waiting.shift(); job; job = waiting.shift()) {
        if (job.signal?.aborted) {
          job.reject(abortError());
          continue;
        }
        await job.run();
      }
    } finally {
      isRunning = false;
    }
  };

  const enqueue = <T>(task: Task<T>, options: EnqueueOptions = {}) =>
    new Promise<T>((resolve, reject) => {
      const job: Job = {
        signal: options.signal,
        reject,
        // 작업 실패는 호출자에게만 전달하고 큐는 다음 작업으로 넘어간다.
        run: async () => {
          try {
            resolve(await task(options.signal));
          } catch (error) {
            reject(error);
          }
        },
      };
      if (options.priority) waiting.unshift(job);
      else waiting.push(job);
      void pump();
    });

  return { enqueue };
};

/**
 * 오더 분석 화면의 모든 통계 요청(카드 요약 · 모두 가져오기 · 팝업 경향)이 지나가는 단일 큐.
 * 운영 DB 로 나가는 분석 요청이 어떤 조합으로도 동시에 2건이 되지 않게 한다.
 */
export const analyticsRequestQueue = createSerialQueue();
